import { Scene, ParticleSystem, Texture, Vector3, Color4, AbstractMesh, Bone, DynamicTexture } from '@babylonjs/core';
import { logger } from '../core/Logger';
import { EventBus } from '../core/EventBus';

// MUST MATCH Blender addon (blender/himum_tools/schema.py)
const ENTITY_PROPERTY_PREFIX = 'hm';

/**
 * Preset configuration for one-shot particle effects
 * These are reusable effect definitions that can be spawned anywhere
 */
export interface ParticlePreset {
    name: string;
    
    // Emission
    emitCount: number;           // Total particles to emit (for burst mode)
    emitRate?: number;           // Particles per second (for continuous mode)
    duration?: number;           // How long to emit (seconds), undefined = single burst
    
    // Particle properties
    lifetime: { min: number; max: number };
    size: { min: number; max: number };
    
    // Velocity
    velocity: { min: number; max: number };
    direction1: Vector3;
    direction2: Vector3;
    gravity?: Vector3;           // Gravity effect on particles
    
    // Appearance
    color1: Color4;
    color2: Color4;
    colorDead: Color4;
    texturePath?: string;
    blendMode?: number;
    
    // Shape
    emitterType?: 'point' | 'box' | 'sphere' | 'cone';
    emitterSize?: Vector3;       // For box emitter
    emitterRadius?: number;      // For sphere/cone emitter
}

/**
 * Get entity property from extras using the standardized prefix
 * Converts 'Emission.rate' → 'hm_Emission_rate'
 */
function getEntityProperty<T = any>(extras: any, propPath: string, defaultValue: T): T {
    if (!extras) return defaultValue;
    const key = `${ENTITY_PROPERTY_PREFIX}_${propPath.replace(/\./g, '_')}`;
    return extras[key] ?? defaultValue;
}

/**
 * Configuration for a particle system parsed from Blender metadata
 */
export interface ParticleSystemConfig {
    name: string;
    
    // Core Properties
    emitRate: number;
    capacity: number;
    
    // Lifetime & Size
    lifetime: { min: number; max: number };
    size: { min: number; max: number };
    
    // Velocity & Direction
    velocity: { min: number; max: number };
    direction1: Vector3;
    direction2: Vector3;
    gravity?: Vector3;
    
    // Appearance
    color1: Color4;
    color2: Color4;
    colorDead: Color4;
    texturePath?: string;
    blendMode?: number;
    
    // Emitter Shape
    emitterType?: 'point' | 'box' | 'sphere';
    emitterSize?: Vector3;
    emitterRadius?: number;
    
    // State
    enabled: boolean;
    
    // Pre-warming
    preWarm?: boolean;
    preWarmCycles?: number;
    
    // Attachment (for character particles)
    attachBone?: string;
    attachMode?: 'rigid' | 'follow';
    attachOffset?: Vector3;
}

/**
 * Internal particle system instance tracking
 */
interface ParticleSystemInstance {
    id: string;
    particleSystem: ParticleSystem;
    emitterMesh: AbstractMesh;
    config: ParticleSystemConfig;
    attachedTo?: {
        character: any; // Character type
        bone: Bone;
        mode: 'rigid' | 'follow';
        offset: Vector3;
        anchorNode?: AbstractMesh; // For follow mode tracking
    };
}

/**
 * Manages all particle systems in the scene
 * - Creates particle systems from level meshes
 * - Handles trigger-based enable/disable
 * - Manages character bone attachments
 * - Updates follow-mode particle positions
 * - Spawns one-shot effects from presets
 */
export class ParticleManager {
    private static instance: ParticleManager | null = null;
    
    private scene: Scene;
    private particleSystems: Map<string, ParticleSystemInstance> = new Map();
    private presets: Map<string, ParticlePreset> = new Map();
    private oneShotSystems: Set<ParticleSystem> = new Set();
    private eventBus: EventBus;

    constructor(scene: Scene, eventBus: EventBus) {
        this.scene = scene;
        this.eventBus = eventBus;
        
        // Set singleton instance
        ParticleManager.instance = this;
        
        // Listen for particle toggle events from triggers
        this.eventBus.on('particle-toggle', this.handleParticleToggle.bind(this));
        
        // Register default presets
        this.registerDefaultPresets();
        
        logger.level.log('🎨 ParticleManager initialized');
    }
    
    /**
     * Get the singleton instance of ParticleManager
     */
    public static getInstance(): ParticleManager | null {
        return ParticleManager.instance;
    }

    /**
     * Register built-in particle presets for common effects
     */
    private registerDefaultPresets(): void {
        // Wood impact - small burst of wood chips
        this.registerPreset({
            name: 'impact_wood',
            emitCount: 15,
            lifetime: { min: 0.3, max: 0.8 },
            size: { min: 0.02, max: 0.06 },
            velocity: { min: 2, max: 5 },
            direction1: new Vector3(-1, 0.5, -1),
            direction2: new Vector3(1, 2, 1),
            gravity: new Vector3(0, -9.8, 0),
            color1: new Color4(0.76, 0.60, 0.42, 1.0),  // Light wood
            color2: new Color4(0.55, 0.40, 0.25, 1.0),  // Dark wood
            colorDead: new Color4(0.4, 0.3, 0.2, 0.0),
            blendMode: ParticleSystem.BLENDMODE_STANDARD,
            emitterType: 'sphere',
            emitterRadius: 0.1,
        });

        // Damage spark - quick flash of particles on hit
        this.registerPreset({
            name: 'damage_spark',
            emitCount: 20,
            lifetime: { min: 0.1, max: 0.3 },
            size: { min: 0.03, max: 0.08 },
            velocity: { min: 3, max: 8 },
            direction1: new Vector3(-1, -0.5, -1),
            direction2: new Vector3(1, 1.5, 1),
            color1: new Color4(1.0, 0.9, 0.5, 1.0),   // Bright yellow
            color2: new Color4(1.0, 0.5, 0.2, 1.0),   // Orange
            colorDead: new Color4(1.0, 0.3, 0.0, 0.0),
            blendMode: ParticleSystem.BLENDMODE_ADD,
            emitterType: 'point',
        });

        // Explosion debris - larger chunks flying outward
        this.registerPreset({
            name: 'explosion_debris',
            emitCount: 30,
            lifetime: { min: 0.5, max: 1.5 },
            size: { min: 0.05, max: 0.15 },
            velocity: { min: 5, max: 12 },
            direction1: new Vector3(-1, 0.2, -1),
            direction2: new Vector3(1, 1, 1),
            gravity: new Vector3(0, -9.8, 0),
            color1: new Color4(0.8, 0.7, 0.6, 1.0),
            color2: new Color4(0.4, 0.35, 0.3, 1.0),
            colorDead: new Color4(0.3, 0.25, 0.2, 0.0),
            blendMode: ParticleSystem.BLENDMODE_STANDARD,
            emitterType: 'sphere',
            emitterRadius: 0.3,
        });

        // Dust puff - soft cloud on impact
        this.registerPreset({
            name: 'dust_puff',
            emitCount: 25,
            lifetime: { min: 0.4, max: 1.0 },
            size: { min: 0.1, max: 0.4 },
            velocity: { min: 0.5, max: 2 },
            direction1: new Vector3(-1, 0.5, -1),
            direction2: new Vector3(1, 1.5, 1),
            color1: new Color4(0.7, 0.65, 0.6, 0.5),
            color2: new Color4(0.5, 0.45, 0.4, 0.3),
            colorDead: new Color4(0.4, 0.35, 0.3, 0.0),
            blendMode: ParticleSystem.BLENDMODE_STANDARD,
            emitterType: 'sphere',
            emitterRadius: 0.2,
        });

        // Hit flash - very brief bright flash
        this.registerPreset({
            name: 'hit_flash',
            emitCount: 10,
            lifetime: { min: 0.05, max: 0.15 },
            size: { min: 0.1, max: 0.25 },
            velocity: { min: 0.5, max: 1.5 },
            direction1: new Vector3(-1, -1, -1),
            direction2: new Vector3(1, 1, 1),
            color1: new Color4(1.0, 1.0, 1.0, 1.0),
            color2: new Color4(1.0, 0.9, 0.7, 0.8),
            colorDead: new Color4(1.0, 0.8, 0.5, 0.0),
            blendMode: ParticleSystem.BLENDMODE_ADD,
            emitterType: 'point',
        });

        // ================================================================
        // Magic Beam Presets for Channeled Attacks
        // ================================================================

        // Ice beam - blue-white particles, high velocity, directional
        this.registerPreset({
            name: 'magic_beam_ice',
            emitCount: 50,
            emitRate: 200, // For continuous emission
            duration: 0.1, // Short bursts
            lifetime: { min: 0.2, max: 0.5 },
            size: { min: 0.05, max: 0.15 },
            velocity: { min: 15, max: 25 },
            direction1: new Vector3(-0.1, -0.1, 1), // Mostly forward
            direction2: new Vector3(0.1, 0.1, 1),
            color1: new Color4(0.6, 0.9, 1.0, 1.0),   // Light ice blue
            color2: new Color4(0.3, 0.6, 1.0, 0.8),   // Deeper blue
            colorDead: new Color4(0.8, 0.95, 1.0, 0.0), // White fade
            blendMode: ParticleSystem.BLENDMODE_ADD,
            emitterType: 'point',
        });

        // Fire beam - orange-red particles with flicker effect
        this.registerPreset({
            name: 'magic_beam_fire',
            emitCount: 60,
            emitRate: 250,
            duration: 0.1,
            lifetime: { min: 0.15, max: 0.4 },
            size: { min: 0.08, max: 0.2 },
            velocity: { min: 12, max: 20 },
            direction1: new Vector3(-0.15, -0.15, 1),
            direction2: new Vector3(0.15, 0.15, 1),
            color1: new Color4(1.0, 0.7, 0.2, 1.0),   // Orange
            color2: new Color4(1.0, 0.3, 0.1, 0.9),   // Red-orange
            colorDead: new Color4(0.8, 0.2, 0.0, 0.0), // Red fade
            blendMode: ParticleSystem.BLENDMODE_ADD,
            emitterType: 'point',
        });

        // Lightning beam - white-blue electric particles
        this.registerPreset({
            name: 'magic_beam_lightning',
            emitCount: 40,
            emitRate: 180,
            duration: 0.05,
            lifetime: { min: 0.1, max: 0.25 },
            size: { min: 0.03, max: 0.1 },
            velocity: { min: 20, max: 35 },
            direction1: new Vector3(-0.2, -0.2, 1),
            direction2: new Vector3(0.2, 0.2, 1),
            color1: new Color4(1.0, 1.0, 1.0, 1.0),   // White
            color2: new Color4(0.6, 0.7, 1.0, 0.9),   // Blue-white
            colorDead: new Color4(0.4, 0.5, 1.0, 0.0),
            blendMode: ParticleSystem.BLENDMODE_ADD,
            emitterType: 'point',
        });

        // Arcane beam - purple magic particles
        this.registerPreset({
            name: 'magic_beam_arcane',
            emitCount: 45,
            emitRate: 200,
            duration: 0.1,
            lifetime: { min: 0.2, max: 0.45 },
            size: { min: 0.06, max: 0.18 },
            velocity: { min: 14, max: 22 },
            direction1: new Vector3(-0.12, -0.12, 1),
            direction2: new Vector3(0.12, 0.12, 1),
            color1: new Color4(0.8, 0.4, 1.0, 1.0),   // Purple
            color2: new Color4(0.5, 0.2, 0.8, 0.85),  // Deep purple
            colorDead: new Color4(0.3, 0.1, 0.5, 0.0),
            blendMode: ParticleSystem.BLENDMODE_ADD,
            emitterType: 'point',
        });

        // ================================================================
        // Burst Effects for Channel Start/End
        // ================================================================

        // Ice burst
        this.registerPreset({
            name: 'ice_burst',
            emitCount: 30,
            lifetime: { min: 0.3, max: 0.6 },
            size: { min: 0.1, max: 0.25 },
            velocity: { min: 3, max: 8 },
            direction1: new Vector3(-1, 0, -1),
            direction2: new Vector3(1, 1, 1),
            color1: new Color4(0.7, 0.9, 1.0, 1.0),
            color2: new Color4(0.4, 0.7, 1.0, 0.8),
            colorDead: new Color4(1.0, 1.0, 1.0, 0.0),
            blendMode: ParticleSystem.BLENDMODE_ADD,
            emitterType: 'sphere',
            emitterRadius: 0.2,
        });

        // Fire burst
        this.registerPreset({
            name: 'fire_burst',
            emitCount: 35,
            lifetime: { min: 0.25, max: 0.5 },
            size: { min: 0.12, max: 0.3 },
            velocity: { min: 4, max: 10 },
            direction1: new Vector3(-1, 0.2, -1),
            direction2: new Vector3(1, 1.5, 1),
            color1: new Color4(1.0, 0.8, 0.3, 1.0),
            color2: new Color4(1.0, 0.4, 0.1, 0.9),
            colorDead: new Color4(0.5, 0.1, 0.0, 0.0),
            blendMode: ParticleSystem.BLENDMODE_ADD,
            emitterType: 'sphere',
            emitterRadius: 0.25,
        });

        // Fire explosion (end effect)
        this.registerPreset({
            name: 'fire_explosion',
            emitCount: 50,
            lifetime: { min: 0.4, max: 0.8 },
            size: { min: 0.15, max: 0.4 },
            velocity: { min: 5, max: 12 },
            direction1: new Vector3(-1, 0, -1),
            direction2: new Vector3(1, 2, 1),
            gravity: new Vector3(0, -5, 0),
            color1: new Color4(1.0, 0.6, 0.2, 1.0),
            color2: new Color4(1.0, 0.2, 0.0, 0.8),
            colorDead: new Color4(0.3, 0.1, 0.0, 0.0),
            blendMode: ParticleSystem.BLENDMODE_ADD,
            emitterType: 'sphere',
            emitterRadius: 0.4,
        });

        // Fairies - white dots dancing randomly like fireflies
        this.registerPreset({
            name: 'fairies',
            emitCount: 20,
            duration: 0, // Continuous effect
            emitRate: 8,
            lifetime: { min: 2.0, max: 4.0 },
            size: { min: 0.03, max: 0.08 },
            velocity: { min: 0.2, max: 0.6 },
            direction1: new Vector3(-1, -0.3, -1),
            direction2: new Vector3(1, 0.5, 1),
            gravity: new Vector3(0, 0.1, 0), // Slight upward drift
            color1: new Color4(1.0, 1.0, 1.0, 0.9),
            color2: new Color4(0.9, 1.0, 0.8, 0.7),
            colorDead: new Color4(1.0, 1.0, 0.9, 0.0),
            blendMode: ParticleSystem.BLENDMODE_ADD,
            emitterType: 'sphere',
            emitterRadius: 2.0,
        });

        // Rising dust - small dust specks rising upward
        this.registerPreset({
            name: 'rising_dust',
            emitCount: 30,
            duration: 0, // Continuous effect
            emitRate: 15,
            lifetime: { min: 1.5, max: 3.0 },
            size: { min: 0.02, max: 0.06 },
            velocity: { min: 0.3, max: 0.8 },
            direction1: new Vector3(-0.2, 0.8, -0.2),
            direction2: new Vector3(0.2, 1.0, 0.2),
            gravity: new Vector3(0, 0.15, 0), // Gentle upward drift
            color1: new Color4(0.8, 0.75, 0.65, 0.5),
            color2: new Color4(0.7, 0.65, 0.55, 0.3),
            colorDead: new Color4(0.6, 0.55, 0.5, 0.0),
            blendMode: ParticleSystem.BLENDMODE_STANDARD,
            emitterType: 'box',
            emitterSize: new Vector3(1.0, 0.1, 1.0),
        });

        logger.level.debug(`🎨 Registered ${this.presets.size} default particle presets`);
    }

    /**
     * Register a particle preset for later use
     */
    public registerPreset(preset: ParticlePreset): void {
        this.presets.set(preset.name, preset);
        logger.level.debug(`🎨 Registered particle preset: ${preset.name}`);
    }

    /**
     * Get a registered preset by name
     */
    public getPreset(name: string): ParticlePreset | undefined {
        return this.presets.get(name);
    }

    /**
     * Get all registered preset names
     */
    public getPresetNames(): string[] {
        return Array.from(this.presets.keys());
    }

    /**
     * Spawn a one-shot particle effect at a position
     * The particle system will automatically clean up after all particles die
     * 
     * @param presetName - Name of registered preset to use
     * @param position - World position to spawn at
     * @param options - Optional overrides for count, scale, etc.
     * @returns The created particle system (for advanced control) or null if preset not found
     */
    public spawnOneShot(
        presetName: string, 
        position: Vector3, 
        options?: {
            countMultiplier?: number;  // Multiply emitCount
            scale?: number;            // Scale size and velocity
            direction?: Vector3;       // Override direction (normalized)
        }
    ): ParticleSystem | null {
        const preset = this.presets.get(presetName);
        if (!preset) {
            logger.level.warn(`🎨 Unknown particle preset: "${presetName}"`);
            return null;
        }

        const id = `oneshot_${presetName}_${Date.now()}`;
        const countMult = options?.countMultiplier ?? 1;
        const scale = options?.scale ?? 1;

        // Create particle system with enough capacity
        const capacity = Math.ceil(preset.emitCount * countMult * 1.5);
        const particleSystem = new ParticleSystem(id, capacity, this.scene);

        // Set position
        particleSystem.emitter = position;

        // Configure from preset
        particleSystem.minLifeTime = preset.lifetime.min;
        particleSystem.maxLifeTime = preset.lifetime.max;
        particleSystem.minSize = preset.size.min * scale;
        particleSystem.maxSize = preset.size.max * scale;
        particleSystem.minEmitPower = preset.velocity.min * scale;
        particleSystem.maxEmitPower = preset.velocity.max * scale;

        // Direction (can be overridden)
        if (options?.direction) {
            const dir = options.direction.normalize();
            particleSystem.direction1 = dir.add(new Vector3(-0.3, -0.3, -0.3));
            particleSystem.direction2 = dir.add(new Vector3(0.3, 0.3, 0.3));
        } else {
            particleSystem.direction1 = preset.direction1;
            particleSystem.direction2 = preset.direction2;
        }

        // Gravity
        if (preset.gravity) {
            particleSystem.gravity = preset.gravity;
        }

        // Colors
        particleSystem.color1 = preset.color1;
        particleSystem.color2 = preset.color2;
        particleSystem.colorDead = preset.colorDead;

        // Blend mode
        particleSystem.blendMode = preset.blendMode ?? ParticleSystem.BLENDMODE_ONEONE;

        // Emitter shape
        switch (preset.emitterType) {
            case 'box':
                const size = preset.emitterSize ?? new Vector3(0.5, 0.5, 0.5);
                particleSystem.minEmitBox = size.scale(-0.5);
                particleSystem.maxEmitBox = size.scale(0.5);
                break;
            case 'sphere':
                particleSystem.createSphereEmitter(preset.emitterRadius ?? 0.1);
                break;
            case 'cone':
                particleSystem.createConeEmitter(preset.emitterRadius ?? 0.5, Math.PI / 6);
                break;
            case 'point':
            default:
                // Point emitter (default)
                break;
        }

        // Create a procedural circle texture using DynamicTexture
        const textureSize = 32;
        const circleTexture = new DynamicTexture(`particle_circle_${Date.now()}`, textureSize, this.scene, false);
        const ctx = circleTexture.getContext();
        
        // Create a radial gradient circle
        const centerX = textureSize / 2;
        const centerY = textureSize / 2;
        const radius = textureSize / 2 - 1;
        
        const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
        gradient.addColorStop(0, 'rgba(255, 255, 255, 1.0)');
        gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.8)');
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0.0)');
        
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, textureSize, textureSize);
        
        circleTexture.update();
        circleTexture.hasAlpha = true;
        particleSystem.particleTexture = circleTexture;

        // Burst mode: emit all particles at once, then stop
        const emitCount = Math.ceil(preset.emitCount * countMult);
        particleSystem.manualEmitCount = emitCount;
        
        // Track for cleanup
        this.oneShotSystems.add(particleSystem);

        // Auto-cleanup after max lifetime + buffer
        const cleanupDelay = (preset.lifetime.max + 0.2) * 1000;
        setTimeout(() => {
            if (this.oneShotSystems.has(particleSystem)) {
                particleSystem.dispose();
                this.oneShotSystems.delete(particleSystem);
            }
        }, cleanupDelay);

        // Start the system
        particleSystem.start();

        logger.level.debug(`🎨 Spawned one-shot "${presetName}" at ${position.toString()} (${emitCount} particles)`);

        return particleSystem;
    }

    /**
     * Create a continuous particle emitter from a preset
     * Unlike spawnOneShot, this creates a persistent emitter that runs until stopped
     * 
     * @param presetName - Name of registered preset to use
     * @param emitter - Mesh or position to emit from
     * @param scene - Scene to create in (optional, uses manager's scene if not provided)
     * @returns The created particle system or null if preset not found
     */
    public createContinuousFromPreset(
        presetName: string,
        emitter: AbstractMesh | Vector3,
        scene?: Scene
    ): ParticleSystem | null {
        const preset = this.presets.get(presetName);
        if (!preset) {
            logger.level.warn(`🎨 Unknown particle preset: "${presetName}"`);
            return null;
        }

        const targetScene = scene ?? this.scene;
        const id = `continuous_${presetName}_${Date.now()}`;
        
        // Use emitRate from preset, or calculate from emitCount if not specified
        const emitRate = preset.emitRate ?? preset.emitCount;
        const capacity = Math.ceil(emitRate * Math.max(preset.lifetime.max, 2) * 1.5);
        
        const particleSystem = new ParticleSystem(id, capacity, targetScene);
        
        // Set emitter
        particleSystem.emitter = emitter;
        
        // Configure from preset
        particleSystem.emitRate = emitRate;
        particleSystem.minLifeTime = preset.lifetime.min;
        particleSystem.maxLifeTime = preset.lifetime.max;
        particleSystem.minSize = preset.size.min;
        particleSystem.maxSize = preset.size.max;
        particleSystem.minEmitPower = preset.velocity.min;
        particleSystem.maxEmitPower = preset.velocity.max;
        
        // Direction
        particleSystem.direction1 = preset.direction1;
        particleSystem.direction2 = preset.direction2;
        
        // Gravity
        if (preset.gravity) {
            particleSystem.gravity = preset.gravity;
        }
        
        // Colors
        particleSystem.color1 = preset.color1;
        particleSystem.color2 = preset.color2;
        particleSystem.colorDead = preset.colorDead;
        
        // Blend mode
        particleSystem.blendMode = preset.blendMode ?? ParticleSystem.BLENDMODE_ADD;
        
        // Emitter shape
        switch (preset.emitterType) {
            case 'box':
                const size = preset.emitterSize ?? new Vector3(0.5, 0.5, 0.5);
                particleSystem.minEmitBox = size.scale(-0.5);
                particleSystem.maxEmitBox = size.scale(0.5);
                break;
            case 'sphere':
                particleSystem.createSphereEmitter(preset.emitterRadius ?? 1.0);
                break;
            case 'cone':
                particleSystem.createConeEmitter(preset.emitterRadius ?? 0.5, Math.PI / 6);
                break;
            case 'point':
            default:
                particleSystem.minEmitBox = Vector3.Zero();
                particleSystem.maxEmitBox = Vector3.Zero();
                break;
        }
        
        // Create procedural circle texture
        const textureSize = 32;
        const circleTexture = new DynamicTexture(`particle_circle_${Date.now()}`, textureSize, targetScene, false);
        const ctx = circleTexture.getContext();
        
        const centerX = textureSize / 2;
        const centerY = textureSize / 2;
        const radius = textureSize / 2 - 1;
        
        const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
        gradient.addColorStop(0, 'rgba(255, 255, 255, 1.0)');
        gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.8)');
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0.0)');
        
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, textureSize, textureSize);
        
        circleTexture.update();
        circleTexture.hasAlpha = true;
        particleSystem.particleTexture = circleTexture;
        
        // Pre-warm by default so particles appear at equilibrium
        particleSystem.preWarmCycles = 100;
        particleSystem.preWarmStepOffset = 10;
        
        logger.level.debug(`🎨 Created continuous emitter from preset "${presetName}" (pre-warmed)`);
        
        return particleSystem;
    }

    /**
     * Parse particle configuration from mesh metadata extras
     */
    public parseParticleConfig(mesh: AbstractMesh, extras: any): ParticleSystemConfig {
        const name = mesh.name;
        
        // Helper to parse Vector3 from array or object
        const parseVector3 = (value: any, defaultVec: Vector3): Vector3 => {
            if (Array.isArray(value) && value.length >= 3) {
                return new Vector3(value[0], value[1], value[2]);
            } else if (value && typeof value === 'object' && 
                       typeof value.x === 'number' && 
                       typeof value.y === 'number' && 
                       typeof value.z === 'number') {
                return new Vector3(value.x, value.y, value.z);
            }
            return defaultVec;
        };
        
        // Helper to parse Color4 from array
        const parseColor4 = (value: any, defaultColor: Color4): Color4 => {
            if (Array.isArray(value)) {
                if (value.length >= 4) {
                    return new Color4(value[0], value[1], value[2], value[3]);
                } else if (value.length >= 3) {
                    return new Color4(value[0], value[1], value[2], 1.0);
                }
            }
            return defaultColor;
        };
        
        // Parse configuration with defaults - read from hm_* custom properties
        const config: ParticleSystemConfig = {
            name,
            
            // Core Properties
            emitRate: getEntityProperty(extras, 'Emission.rate', 100),
            capacity: 1000,
            
            // Lifetime & Size
            lifetime: {
                min: getEntityProperty(extras, 'Lifetime.minLifeTime', 1.0),
                max: getEntityProperty(extras, 'Lifetime.maxLifeTime', 3.0)
            },
            size: {
                min: getEntityProperty(extras, 'Appearance.minSize', 0.1),
                max: getEntityProperty(extras, 'Appearance.maxSize', 0.5)
            },
            
            // Velocity & Direction
            velocity: {
                min: getEntityProperty(extras, 'Emission.minEmitPower', 0.5),
                max: getEntityProperty(extras, 'Emission.maxEmitPower', 2.0)
            },
            direction1: parseVector3(getEntityProperty(extras, 'Emission.direction1', null), new Vector3(0, 1, 0)),
            direction2: parseVector3(getEntityProperty(extras, 'Emission.direction2', null), new Vector3(0, 1, 0)),
            
            // Appearance
            color1: parseColor4(getEntityProperty(extras, 'Appearance.color1', null), new Color4(1, 1, 1, 1)),
            color2: parseColor4(getEntityProperty(extras, 'Appearance.color2', null), new Color4(1, 1, 1, 1)),
            colorDead: parseColor4(getEntityProperty(extras, 'Appearance.colorDead', null), new Color4(1, 1, 1, 0)),
            texturePath: getEntityProperty(extras, 'Appearance.texture', undefined),
            blendMode: ParticleSystem.BLENDMODE_ONEONE,
            
            // Emitter Shape
            emitterType: getEntityProperty(extras, 'Shape.type', 'box') as 'point' | 'box' | 'sphere',
            emitterSize: parseVector3(getEntityProperty(extras, 'Shape.boxSize', null), new Vector3(1, 1, 1)),
            emitterRadius: getEntityProperty(extras, 'Shape.sphereRadius', 1.0),
            
            // State
            enabled: getEntityProperty(extras, 'enabled', true),
            
            // Attachment (if these properties exist in schema)
            attachBone: getEntityProperty(extras, 'attachBone', undefined),
            attachMode: getEntityProperty(extras, 'attachMode', 'rigid') as 'rigid' | 'follow',
            attachOffset: parseVector3(getEntityProperty(extras, 'attachOffset', null), Vector3.Zero())
        };
        
        logger.level.debug(`🎨 Parsed particle config: ${name}`, {
            emitRate: config.emitRate,
            capacity: config.capacity,
            enabled: config.enabled,
            attachBone: config.attachBone
        });
        
        return config;
    }

    /**
     * Create a particle system from a mesh and configuration
     */
    public createParticleSystem(mesh: AbstractMesh, config: ParticleSystemConfig): void {
        const id = config.name;
        
        if (this.particleSystems.has(id)) {
            logger.level.warn(`🎨 Particle system "${id}" already exists, skipping`);
            return;
        }
        
        logger.level.log(`🎨 Creating particle system: ${id}`);
        
        // Create BabylonJS ParticleSystem
        const particleSystem = new ParticleSystem(id, config.capacity, this.scene);
        
        // Set emitter (mesh position)
        particleSystem.emitter = mesh.position;
        
        // Core properties
        particleSystem.emitRate = config.emitRate;
        particleSystem.minLifeTime = config.lifetime.min;
        particleSystem.maxLifeTime = config.lifetime.max;
        particleSystem.minSize = config.size.min;
        particleSystem.maxSize = config.size.max;
        particleSystem.minEmitPower = config.velocity.min;
        particleSystem.maxEmitPower = config.velocity.max;
        
        // Direction
        particleSystem.direction1 = config.direction1;
        particleSystem.direction2 = config.direction2;
        
        // Colors
        particleSystem.color1 = config.color1;
        particleSystem.color2 = config.color2;
        particleSystem.colorDead = config.colorDead;
        
        // Blend mode
        particleSystem.blendMode = config.blendMode || ParticleSystem.BLENDMODE_ONEONE;
        
        // Texture
        if (config.texturePath) {
            try {
                particleSystem.particleTexture = new Texture(config.texturePath, this.scene);
                logger.level.debug(`   ✅ Loaded texture: ${config.texturePath}`);
            } catch (error) {
                logger.level.warn(`   ⚠️ Failed to load texture "${config.texturePath}", using default`, error);
            }
        } else {
            // Try to use mesh material texture as fallback
            const material = mesh.material as any;
            if (material && material.albedoTexture) {
                particleSystem.particleTexture = material.albedoTexture;
                logger.level.debug(`   ✅ Using mesh material texture`);
            } else {
                // Default to a simple white texture
                particleSystem.particleTexture = new Texture('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==', this.scene);
            }
        }
        
        // Emitter type
        switch (config.emitterType) {
            case 'box':
                particleSystem.minEmitBox = config.emitterSize!.scale(-0.5);
                particleSystem.maxEmitBox = config.emitterSize!.scale(0.5);
                break;
            case 'sphere':
                particleSystem.emitter = mesh; // Use mesh as emitter for sphere
                particleSystem.createSphereEmitter(config.emitterRadius!);
                break;
            case 'point':
            default:
                // Point emitter (default, already set)
                break;
        }
        
        // Start or stop based on enabled state
        if (config.enabled) {
            particleSystem.start();
            logger.level.debug(`   ✅ Particle system started`);
        } else {
            logger.level.debug(`   ⏸️ Particle system created but not started (enabled=false)`);
        }
        
        // Store instance
        const instance: ParticleSystemInstance = {
            id,
            particleSystem,
            emitterMesh: mesh,
            config
        };
        
        this.particleSystems.set(id, instance);
        
        logger.level.log(`✅ Particle system "${id}" created successfully`);
    }

    /**
     * Enable a particle system
     */
    public enableParticleSystem(id: string): void {
        const instance = this.particleSystems.get(id);
        if (!instance) {
            logger.level.warn(`🎨 Cannot enable: particle system "${id}" not found`);
            return;
        }
        
        if (!instance.particleSystem.isStarted()) {
            instance.particleSystem.start();
            instance.config.enabled = true;
            logger.level.log(`🎨 Enabled particle system: ${id}`);
        }
    }

    /**
     * Disable a particle system
     */
    public disableParticleSystem(id: string): void {
        const instance = this.particleSystems.get(id);
        if (!instance) {
            logger.level.warn(`🎨 Cannot disable: particle system "${id}" not found`);
            return;
        }
        
        if (instance.particleSystem.isStarted()) {
            instance.particleSystem.stop();
            instance.config.enabled = false;
            logger.level.log(`🎨 Disabled particle system: ${id}`);
        }
    }

    /**
     * Toggle a particle system on/off
     */
    public toggleParticleSystem(id: string): void {
        const instance = this.particleSystems.get(id);
        if (!instance) {
            logger.level.warn(`🎨 Cannot toggle: particle system "${id}" not found`);
            return;
        }
        
        if (instance.particleSystem.isStarted()) {
            this.disableParticleSystem(id);
        } else {
            this.enableParticleSystem(id);
        }
    }

    /**
     * Handle particle toggle events from triggers
     */
    private handleParticleToggle(data: { particleId: string; action: 'enable' | 'disable' | 'toggle' }): void {
        logger.level.debug(`🎨 Particle toggle event: ${data.action} ${data.particleId}`);
        
        switch (data.action) {
            case 'enable':
                this.enableParticleSystem(data.particleId);
                break;
            case 'disable':
                this.disableParticleSystem(data.particleId);
                break;
            case 'toggle':
                this.toggleParticleSystem(data.particleId);
                break;
        }
    }

    /**
     * Attach particle system to a character bone
     * @param particleId - ID of the particle system
     * @param character - Character instance
     * @param bone - Bone to attach to
     * @param mode - 'rigid' (direct parent) or 'follow' (smoothed tracking)
     * @param offset - Position offset from bone
     */
    public attachParticleToCharacter(
        particleId: string,
        character: any, // Will type properly when integrating with Character
        bone: Bone,
        mode: 'rigid' | 'follow' = 'rigid',
        offset: Vector3 = Vector3.Zero()
    ): void {
        const instance = this.particleSystems.get(particleId);
        if (!instance) {
            logger.level.warn(`🎨 Cannot attach: particle system "${particleId}" not found`);
            return;
        }
        
        logger.level.log(`🎨 Attaching particle "${particleId}" to bone "${bone.name}" [${mode} mode]`);
        
        if (mode === 'rigid') {
            // Rigid attachment: Direct bone parenting
            const anchor = instance.emitterMesh;
            anchor.setParent(bone.getTransformNode() || null);
            anchor.position = offset;
        } else {
            // Follow mode: Track bone position with smoothing (updated in update loop)
            // Create anchor node for follow tracking
            const anchorNode = instance.emitterMesh;
            anchorNode.position = bone.getAbsolutePosition().add(offset);
        }
        
        instance.attachedTo = {
            character,
            bone,
            mode,
            offset,
            anchorNode: mode === 'follow' ? instance.emitterMesh : undefined
        };
        
        logger.level.log(`   ✅ Particle attached successfully`);
    }

    /**
     * Detach particle system from character
     */
    public detachParticle(particleId: string): void {
        const instance = this.particleSystems.get(particleId);
        if (!instance || !instance.attachedTo) {
            return;
        }
        
        logger.level.log(`🎨 Detaching particle "${particleId}"`);
        
        // Remove parenting
        instance.emitterMesh.setParent(null);
        
        // Clear attachment data
        instance.attachedTo = undefined;
    }

    /**
     * Update follow-mode particle positions
     * Should be called every frame from main game loop
     */
    public update(_deltaTime: number): void {
        for (const instance of this.particleSystems.values()) {
            if (instance.attachedTo && instance.attachedTo.mode === 'follow') {
                const { bone, offset, anchorNode } = instance.attachedTo;
                
                if (!anchorNode) continue;
                
                // Get target position (bone + offset)
                const targetPos = bone.getAbsolutePosition().add(offset);
                
                // Smooth interpolation (adjust 0.1 for different smoothing speeds)
                const smoothing = 0.1;
                anchorNode.position = Vector3.Lerp(
                    anchorNode.position,
                    targetPos,
                    smoothing
                );
                
                // Update particle emitter to new position
                instance.particleSystem.emitter = anchorNode.position;
            }
        }
    }

    /**
     * Get particle system by ID
     */
    public getParticleSystem(id: string): ParticleSystem | null {
        return this.particleSystems.get(id)?.particleSystem || null;
    }

    /**
     * Get all particle system IDs
     */
    public getParticleSystemIds(): string[] {
        return Array.from(this.particleSystems.keys());
    }

    /**
     * Clear all particle systems (call on level unload).
     * Unlike dispose, this keeps the manager operational for reuse.
     */
    public clear(): void {
        logger.level.log('🎨 Clearing particle systems...');
        
        for (const instance of this.particleSystems.values()) {
            instance.particleSystem.dispose();
        }
        
        this.particleSystems.clear();
        
        logger.level.log('✅ Particle systems cleared');
    }

    /**
     * Clean up all particle systems
     */
    public dispose(): void {
        logger.level.log('🎨 Disposing ParticleManager...');
        
        for (const instance of this.particleSystems.values()) {
            instance.particleSystem.dispose();
        }
        
        this.particleSystems.clear();
        this.eventBus.off('particle-toggle', this.handleParticleToggle.bind(this));
        
        logger.level.log('✅ ParticleManager disposed');
    }
}
